import React, { useMemo } from 'react';
import ImpressionTimeline from '../../../../../../shared/designSystem/components/ImpressionTimeline';
import { getMobileOperatingSystem, determineAdServer } from './utils';

// =============================================================================
// ImpressionsTimeLine (V1 Transactions) — facelifted off the local AntD
// `Timeline` + `Modal` + 4-column `Row/Col` chrome to the shared
// `<ImpressionTimeline/>` primitive (already in use on V2 SignUps + V2
// Transactions + V1 SignUps + V1 Sessions expand rows).
//
// Macro-resolution rules from the legacy implementation are preserved
// verbatim in `normalizeImpression` below as a pure transformation step.
// All ad-server detection (TTD/CENTRO/LiquidM legacy tags), app-store /
// sitename fallback chains, OS classification, and the `publisherInfo`
// lookup ladder for missing app names + site names are unchanged. Only
// the rendering chrome moves.
// =============================================================================

const safeDecode = (s) => {
  try {
    return decodeURIComponent(s);
  } catch (e) {
    return s;
  }
};

const isValidUrl = (str) => {
  try {
    const hasScheme = /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(str);
    const url = new URL(hasScheme ? str : `https://${str}`);
    return !!url.hostname;
  } catch (e) {
    return false;
  }
};

// Pure transformation — V1 raw impression row → primitive's expected shape.
// Logic preserved from the legacy `makeTimeLine` mapper, including the
// `publisherInfo` lookup paths the V1 transactions build added on top of
// the SignUps version.
const normalizeImpression = (impression, i, publisherInfo) => {
  const {
    IMPRESSION_TIME,
    IMPRESSION_USERAGENT,
    PAGE_URLHOST,
    IMPRESSION_REFERRER,
    IMPRESSION_PUBLISHERNAME,
    IMPRESSION_APPNAME,
    IMPRESSION_APPSTORE_ID,
    IMPRESSION_SITE_ID,
    IMPRESSION_SITENAME,
    CREATIVEID,
    CREATIVEURL,
  } = impression;

  const OS = IMPRESSION_USERAGENT ? getMobileOperatingSystem(IMPRESSION_USERAGENT) : 'Unknown';

  let appSiteName = '';
  let appSiteLink = '';
  let applicationPlatform = '';

  if (
    (!IMPRESSION_APPSTORE_ID ||
      IMPRESSION_APPSTORE_ID === 'N/A' ||
      IMPRESSION_APPSTORE_ID === 'null') &&
    (!IMPRESSION_APPNAME || IMPRESSION_APPNAME === 'N/A') &&
    (!IMPRESSION_SITE_ID || IMPRESSION_SITE_ID === 'N/A') &&
    (!IMPRESSION_SITENAME || IMPRESSION_SITENAME === 'N/A')
  ) {
    appSiteName = PAGE_URLHOST ? `Served on ${safeDecode(PAGE_URLHOST)}` : '';
    appSiteLink = IMPRESSION_REFERRER ? safeDecode(IMPRESSION_REFERRER) : '';
    applicationPlatform = PAGE_URLHOST ? determineAdServer(PAGE_URLHOST) : '';
    if (IMPRESSION_REFERRER && applicationPlatform !== 'Application') {
      applicationPlatform = determineAdServer(IMPRESSION_REFERRER);
    }
  } else if (
    (IMPRESSION_APPSTORE_ID || IMPRESSION_APPSTORE_ID !== 'null') &&
    IMPRESSION_APPNAME
  ) {
    const decodedAppName = safeDecode(IMPRESSION_APPNAME)
      .replace(/[+_]/g, ' ')
      .split(':')
      .pop();
    appSiteName = `Served on ${decodedAppName}`;
    appSiteLink = safeDecode(IMPRESSION_APPSTORE_ID);
    applicationPlatform = 'Application';
  } else if (
    IMPRESSION_APPSTORE_ID &&
    !IMPRESSION_APPNAME &&
    publisherInfo &&
    publisherInfo.length > 0 &&
    IMPRESSION_APPSTORE_ID !== 'null'
  ) {
    // V1 transactions extra: when we only have an app store ID, look up
    // the title from the publisherInfo array supplied by the loader.
    const pubInfo = publisherInfo.find(
      (p) => p.IMPRESSION_APPSTORE_ID === IMPRESSION_APPSTORE_ID
    );
    const appName = pubInfo && pubInfo.title ? pubInfo.title : IMPRESSION_APPSTORE_ID;
    appSiteName = `Served on ${appName}`;
    appSiteLink = safeDecode(IMPRESSION_APPSTORE_ID);
    applicationPlatform = 'Application';
  } else if (IMPRESSION_SITENAME && !IMPRESSION_SITE_ID) {
    appSiteName = `Served on ${safeDecode(IMPRESSION_SITENAME)
      .replace(/[+_]/g, ' ')
      .split(':')
      .pop()}`;
    appSiteLink = IMPRESSION_REFERRER || '';
    applicationPlatform = 'Application';
  } else if (IMPRESSION_SITENAME || IMPRESSION_SITE_ID) {
    // V1 transactions extra: pubInfo lookup by site id or sitename.
    const pubInfo = (publisherInfo || []).find(
      (p) =>
        p.IMPRESSION_SITE_ID === IMPRESSION_SITE_ID ||
        p.IMPRESSION_SITENAME === IMPRESSION_SITENAME
    );
    let tempSiteName = pubInfo && pubInfo.title ? pubInfo.title : null;

    if (!tempSiteName) {
      tempSiteName = safeDecode(IMPRESSION_SITENAME || IMPRESSION_SITE_ID)
        .replace(/[+_]/g, ' ')
        .split(':')
        .pop()
        .replace(/^\/+|\/+$/g, '');

      if (determineAdServer(tempSiteName) === 'Website' && isValidUrl(tempSiteName)) {
        const hasScheme = /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(tempSiteName);
        const url = new URL(hasScheme ? tempSiteName : `https://${tempSiteName}`);
        tempSiteName = url.host;
      }
    }

    appSiteName = `${
      determineAdServer(tempSiteName) === 'Ad Server' ? 'Served via' : 'Served on'
    } ${tempSiteName}`;
    appSiteLink = IMPRESSION_REFERRER ? IMPRESSION_REFERRER : `https://${tempSiteName.trim()}`;
    applicationPlatform = 'Website';
  }

  const platform =
    OS && OS !== 'Unknown' && OS !== 'Computer' ? OS : applicationPlatform || OS;

  // Publisher name resolution: prefer the raw `IMPRESSION_PUBLISHERNAME`
  // macro. When that's missing but `appSiteName` has resolved, promote the
  // resolved site/app name to be the publisher label and clear the media
  // descriptor so the same string doesn't appear twice in the publisher
  // block.
  let publisher;
  let mediaDescriptor = appSiteName;
  if (IMPRESSION_PUBLISHERNAME && IMPRESSION_PUBLISHERNAME !== 'N/A') {
    publisher = safeDecode(IMPRESSION_PUBLISHERNAME).replace(/[+_]/g, ' ');
  } else if (appSiteName) {
    publisher = appSiteName.replace(/^Served on\s*-?\s*/i, '');
    mediaDescriptor = '';
  } else {
    publisher = 'Unknown publisher';
  }

  // Publisher logo lookup — same source the Publishers card on this tab
  // uses (`chartDataRaw.transactionsPublishers[].mediaSource`). Match by
  // app store id, site id, or sitename so the logo for known publishers
  // surfaces in the impression timeline too. Both sides must hold REAL
  // values (not falsy, 'N/A', 'null', 'undefined') so placeholder fields
  // on multiple records don't false-match each other and lend the wrong
  // publisher's logo to an unrelated site. Also require the candidate
  // publisher to have a real `mediaSource` — no point matching a publisher
  // that doesn't have a logo to share.
  const isReal = (v) =>
    v && v !== 'N/A' && v !== 'null' && v !== 'undefined';
  const matchedPubInfo = (publisherInfo || []).find(
    (p) =>
      isReal(p.mediaSource) &&
      (
        (isReal(IMPRESSION_APPSTORE_ID) &&
          isReal(p.IMPRESSION_APPSTORE_ID) &&
          p.IMPRESSION_APPSTORE_ID === IMPRESSION_APPSTORE_ID) ||
        (isReal(IMPRESSION_SITE_ID) &&
          isReal(p.IMPRESSION_SITE_ID) &&
          p.IMPRESSION_SITE_ID === IMPRESSION_SITE_ID) ||
        (isReal(IMPRESSION_SITENAME) &&
          isReal(p.IMPRESSION_SITENAME) &&
          p.IMPRESSION_SITENAME === IMPRESSION_SITENAME)
      )
  );
  const impressionPublisherMedia =
    (matchedPubInfo && matchedPubInfo.mediaSource) || undefined;

  return {
    eventId: CREATIVEID || `imp-${i}`,
    impressionTime: IMPRESSION_TIME,
    impressionPublisher: publisher,
    impressionPublisherUrl: appSiteLink || '',
    impressionPublisherPlatform: platform,
    impressionPublisherMedia,
    platformTooltip: IMPRESSION_USERAGENT || platform,
    creativeUrl: CREATIVEURL || undefined,
    media: mediaDescriptor || '',
  };
};

const ImpressionSubTable = ({ impressions = [], publisherInfo = [] }) => {
  const normalized = useMemo(
    () => (impressions || []).map((imp, i) => normalizeImpression(imp, i, publisherInfo)),
    [impressions, publisherInfo]
  );

  return (
    <ImpressionTimeline
      impressions={normalized}
      sort="newest"
      showMediaRow
      showCreativeThumb
    />
  );
};

export default ImpressionSubTable;
